import { useEffect } from "react";

import { getVisibleLiveReviewIssues, type LiveReviewData } from "@common/schemas/live-review";
import { buildActionId, liveReviewIssueKey } from "@common/utils/liveReviewOoxml";

import type { RuleReportEntry } from "../utils/rules";
import type { RedlineUserGestureSurface } from "../utils/redline-apply-outcome";
import { recordHowManyRedlinesWereGenerated } from "../utils/redline-apply-recorder";

export interface ReviewReportRedlinePosition {
    reviewId: string;
    entryIndex: number;
    actionIndex: number;
    stepIndex: number;
    ooxmlIndex: number;
}

export function buildReviewReportRedlineId({
    reviewId,
    entryIndex,
    actionIndex,
    stepIndex,
    ooxmlIndex,
}: ReviewReportRedlinePosition): string {
    return `review_report:${reviewId}:${entryIndex}:${actionIndex}:${stepIndex}:${ooxmlIndex}`;
}

export interface ListLiveReviewRedlineIdsArgs {
    snapshot: LiveReviewData;
    messageId: string | undefined;
}

export function listLiveReviewRedlineIds({
    snapshot,
    messageId,
}: ListLiveReviewRedlineIdsArgs): string[] {
    const ids: string[] = [];
    const stableId = messageId ?? snapshot.reviewId ?? undefined;

    for (const issue of getVisibleLiveReviewIssues(snapshot)) {
        for (const remedy of issue.remedies ?? []) {
            ids.push(buildActionId(stableId, liveReviewIssueKey(issue), "remedy", remedy.id));
        }
    }

    return ids;
}

export interface ListReviewReportRedlineIdsArgs {
    reviewId: string;
    reviewReport: RuleReportEntry[];
}

export function listReviewReportRedlineIds({
    reviewId,
    reviewReport,
}: ListReviewReportRedlineIdsArgs): string[] {
    const ids: string[] = [];

    reviewReport.forEach((entry, entryIndex) => {
        (entry?.action_plan ?? []).forEach((action, actionIndex) => {
            (action?.steps ?? []).forEach((step, stepIndex) => {
                const ooxmlAfter = step?.action_report?.target_ooxml_after;
                if (!Array.isArray(ooxmlAfter)) return;
                ooxmlAfter.forEach((_, ooxmlIndex) => {
                    ids.push(buildReviewReportRedlineId({
                        reviewId,
                        entryIndex,
                        actionIndex,
                        stepIndex,
                        ooxmlIndex,
                    }));
                });
            });
        });
    });

    return ids;
}

export interface GeneratedRedlines {
    initiatedFrom: RedlineUserGestureSurface;
    reviewId: string | null;
    redlineIds: string[];
}

const reportedRedlineIds = new Set<string>();

export function useReportGeneratedRedlines({
    initiatedFrom,
    reviewId,
    redlineIds,
}: GeneratedRedlines): void {
    useEffect(() => {
        let generated = 0;
        for (const id of redlineIds) {
            if (reportedRedlineIds.has(id)) continue;
            reportedRedlineIds.add(id);
            generated += 1;
        }
        if (generated === 0) return;

        void recordHowManyRedlinesWereGenerated({
            source: { initiatedFrom, reviewId },
            generated,
        });
    }, [initiatedFrom, reviewId, redlineIds]);
}
